import { useEffect, useState } from "react";
import { Alert, AlertTitle, Chip, Paper, Stack, Typography } from "@mui/material";
import { EmptyState, Loading } from "@lopie/kernel-ui";

import { api } from "../lib/oncall-api.ts";
import { useDirectory } from "../lib/people.ts";

/**
 * Who is on call.
 *
 * Each rotation leads with who it would ring *right now* rather than with a
 * calendar. A calendar is what you read when planning; "who answers if I page
 * this" is what you read the rest of the time, and only one of the two can be
 * wrong in a way that costs anything.
 *
 * A rotation resolving to nobody is said in the refused colour, because it is
 * a fault and not an empty state: a level pointed at it escalates past in
 * silence, which is the failure mode this whole screen exists to make visible
 * before an incident finds it.
 */
export default function Rotations() {
  // Both things this screen renders about a person were ids: who is on call
  // right now, and the order underneath it. A board of ULIDs answers "who
  // answers if I page this" with a string nobody can read (LO-107).
  const directory = useDirectory();
  const [rotations, setRotations] = useState(null);
  const [detail, setDetail] = useState({});
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    let live = true;
    api.schedules()
      .then(async (body) => {
        const list = body.schedules ?? [];
        if (!live) return;
        setRotations(list);
        const found = {};
        for (const rotation of list) {
          try {
            found[rotation.id] = await api.schedule(rotation.id);
          } catch {
            // One rotation failing to resolve must not blank the others: the
            // card renders without an answer rather than the page erroring.
          }
        }
        if (live) setDetail(found);
      })
      .catch((e) => live && setFailure(e.message));
    return () => { live = false; };
  }, []);

  if (failure) {
    return (
      <Alert severity="error" variant="outlined">
        <AlertTitle>Could not read rotations</AlertTitle>
        {failure}
      </Alert>
    );
  }
  if (!rotations) return <Loading label="Reading rotations" />;

  if (rotations.length === 0) {
    return (
      <EmptyState title="No rotations yet">
        <Typography variant="body2">
          A rotation is an ordered list of people and a handoff cadence. Until
          one exists, an escalation level pointed at a rotation resolves to
          nobody — and says so on the incident rather than paging in silence.
        </Typography>
      </EmptyState>
    );
  }

  return (
    <Stack spacing={2}>
      {rotations.map((rotation) => {
        const found = detail[rotation.id];
        const onCall = found?.onCallNow;
        return (
          <Paper key={rotation.id} variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "baseline", flexWrap: "wrap" }}
            >
              <Typography variant="h2" sx={{ flexGrow: 1 }}>
                {rotation.name}
              </Typography>
              <Chip size="small" variant="outlined" label={rotation.timezone} />
              <Chip size="small" variant="outlined" label={rotation.rotationType} />
            </Stack>

            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block", mt: 1.5 }}
            >
              On call right now
            </Typography>
            {onCall ? (
              <Typography>{directory.nameOf(onCall)}</Typography>
            ) : (
              <Typography variant="body2" sx={{ color: "kernel.refused" }}>
                Nobody. A page routed here would escalate past this level in
                silence.
              </Typography>
            )}

            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block", mt: 1.5 }}
            >
              In order
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
              {(found?.members ?? []).map((member, n) => (
                <Chip
                  key={member}
                  size="small"
                  variant="outlined"
                  label={`${n + 1}. ${directory.nameOf(member)}`}
                />
              ))}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}
